"use client"

import { useState } from "react"
import {
  ArrowRightIcon,
  CheckIcon,
  CircleEqualIcon,
  HomeIcon,
  RotateCcwIcon,
  SendIcon,
  ShieldCheckIcon,
} from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import type { AnswerValue } from "@/lib/domain"

type ViewState = "question" | "submitting" | "thanks" | "recorded" | "error"

type InitialResponse =
  | { status: "owned"; answerUid: string; answer: AnswerValue }
  | { status: "recorded" }
  | { status: "capped" }

export function ScanResponse({
  eventUid,
  senderName,
  tokenCode,
  initialResponse,
}: {
  eventUid: string
  senderName: string
  tokenCode: string
  initialResponse?: InitialResponse
}) {
  const [view, setView] = useState<ViewState>(
    initialResponse?.status === "owned"
      ? "thanks"
      : initialResponse?.status === "capped"
        ? "recorded"
        : "question"
  )
  const [showPriorWarning, setShowPriorWarning] = useState(
    initialResponse?.status === "recorded"
  )
  const [answer, setAnswer] = useState<AnswerValue | null>(
    initialResponse?.status === "owned" ? initialResponse.answer : null
  )
  const [answerUid, setAnswerUid] = useState<string | null>(
    initialResponse?.status === "owned" ? initialResponse.answerUid : null
  )
  const [pendingAnswer, setPendingAnswer] = useState<AnswerValue | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function submit(nextAnswer: AnswerValue) {
    setView("submitting")
    setPendingAnswer(nextAnswer)
    setError(null)
    const nextAnswerUid = crypto.randomUUID()

    try {
      const response = await fetch("/api/answers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventUid,
          answerUid: nextAnswerUid,
          answer: nextAnswer,
          token: tokenCode,
          supersedesAnswerUid: answerUid ?? undefined,
        }),
      })
      const result = (await response.json()) as {
        answerUid?: string
        answer?: AnswerValue
        error?: string
      }

      if (response.status === 429) {
        setView("recorded")
        return
      }
      if (!response.ok || !result.answerUid || !result.answer) {
        throw new Error(result.error ?? "Your answer could not be saved.")
      }

      setAnswerUid(result.answerUid)
      setAnswer(result.answer)
      setPendingAnswer(null)
      setShowPriorWarning(false)
      setView("thanks")
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Your answer could not be saved."
      )
      setPendingAnswer(null)
      setView("error")
    }
  }

  if (view === "recorded") {
    return (
      <Card className="response-card w-full" aria-live="polite">
        <CardHeader>
          <div className="response-seal response-seal-private mb-5">
            <ShieldCheckIcon aria-hidden="true" />
          </div>
          <p className="eyebrow">Response already recorded</p>
          <CardTitle>
            <h1 className="font-heading text-4xl leading-[0.98] tracking-[-0.04em] sm:text-5xl">
              An answer has already been recorded for this letter.
            </h1>
          </CardTitle>
          <CardDescription className="text-base leading-7">
            This letter has reached the limit of three answers in 24 hours. No
            further answer can be submitted right now.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="recorded-answer recorded-answer-private">
            <span aria-hidden="true">
              <ShieldCheckIcon />
            </span>
            <div>
              <p className="eyebrow">Privacy protected</p>
              <p className="mt-1 font-medium">
                Please try again after the 24-hour window has passed.
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <p className="choice-note">
            <CircleEqualIcon aria-hidden="true" />
            <span>
              This limit protects the service from repeated or automated
              submissions.
            </span>
          </p>
        </CardFooter>
      </Card>
    )
  }

  if (view === "thanks" && answer) {
    return (
      <Card className="response-card w-full" aria-live="polite">
        <CardHeader>
          <div className="response-seal mb-5">
            <CheckIcon aria-hidden="true" />
          </div>
          <p className="eyebrow">Response recorded</p>
          <CardTitle>
            <h1 className="font-heading text-4xl leading-[0.98] tracking-[-0.04em] sm:text-5xl">
              Thank you. That helps.
            </h1>
          </CardTitle>
          <CardDescription className="text-base leading-7">
            Your answer has been recorded for the sender to review. Letterback
            does not make an automatic decision about the address.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="recorded-answer">
            <span aria-hidden="true">
              <CheckIcon />
            </span>
            <div>
              <p className="eyebrow">Your answer</p>
              <p className="mt-1 font-medium">
                {answer === "moved_away"
                  ? "They’ve moved away"
                  : "They still live here"}
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="justify-between gap-4">
          <span className="text-xs text-muted-foreground">Made a mistake?</span>
          <Button variant="ghost" onClick={() => setView("question")}>
            <RotateCcwIcon data-icon="inline-start" />
            Change my answer
          </Button>
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card className="response-card w-full">
      <CardHeader>
        <div className="question-meta flex items-center justify-between gap-4">
          <Badge variant="secondary">One quick question</Badge>
          <span className="text-xs text-muted-foreground">{senderName}</span>
        </div>
        <CardTitle>
          <h1 className="font-heading text-4xl leading-[0.98] tracking-[-0.045em] text-balance sm:text-5xl">
            Does the person named on the envelope still live at this address?
          </h1>
        </CardTitle>
        <CardDescription className="pt-2 text-base leading-7">
          Choose the answer that matches the post in your hand. We will not ask
          who you are.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {showPriorWarning ? (
          <Alert className="mb-4">
            <ShieldCheckIcon aria-hidden="true" />
            <AlertTitle>Answer already recorded</AlertTitle>
            <AlertDescription>
              An answer has already been recorded for this letter. Only answer
              again if something has changed.
            </AlertDescription>
          </Alert>
        ) : null}
        <div
          className="grid gap-3"
          role="group"
          aria-label="Choose one answer"
          aria-busy={view === "submitting"}
        >
          <Button
            variant="outline"
            size="lg"
            className="answer-button w-full text-left"
            disabled={view === "submitting"}
            onClick={() => submit("moved_away")}
          >
            {pendingAnswer === "moved_away" ? (
              <span className="answer-icon">
                <Spinner />
              </span>
            ) : (
              <span className="answer-icon">
                <SendIcon />
              </span>
            )}
            They&apos;ve moved away
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="answer-button w-full text-left"
            disabled={view === "submitting"}
            onClick={() => submit("still_resident")}
          >
            {pendingAnswer === "still_resident" ? (
              <span className="answer-icon">
                <Spinner />
              </span>
            ) : (
              <span className="answer-icon">
                <HomeIcon />
              </span>
            )}
            They still live here
            <ArrowRightIcon data-icon="inline-end" />
          </Button>
        </div>

        {view === "error" ? (
          <Alert variant="destructive" className="mt-4">
            <AlertTitle>That did not save</AlertTitle>
            <AlertDescription>
              {error} Please choose your answer again.
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>

      <CardFooter>
        <p className="choice-note">
          <CircleEqualIcon aria-hidden="true" />
          <span>
            Both answers are equally useful. You can correct your choice
            afterward from this browser.
          </span>
        </p>
      </CardFooter>
    </Card>
  )
}
